import { useEffect, useMemo, useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { useStore } from "./StoreContext";
import { storeApi, loadCheckoutForm, saveCheckoutForm } from "./storeApi";
import { money, countryName, flag } from "./format";
import { useQuote } from "./CartPage";
import ShippingIcon, { deliveryInfo } from "./ShippingIcon";
import PacketaPicker from "./PacketaPicker";
import { Checkbox, Select } from "./controls";
import Icon from "./Icons";
import InquiryForm from "./InquiryForm";
import useSeo from "./useSeo";

// Destinations from the shop's config, home first.
function shipCountries(config) {
  const list = config.countries?.length ? config.countries : [{ code: "CZ", methods: config.shippingMethods || [], pickupVendors: [] }];
  return [...list].sort((a, b) => (a.code === "CZ" ? -1 : b.code === "CZ" ? 1 : countryName(a.code).localeCompare(countryName(b.code))));
}

// Start from the visitor's own country when we ship there.
function guessCountry(countries, ipCountry) {
  if (ipCountry && countries.some((c) => c.code === ipCountry)) return ipCountry;
  try {
    const region = (navigator.languages || [navigator.language])
      .map((l) => String(l).split("-")[1])
      .find((r) => r && countries.some((c) => c.code === r.toUpperCase()));
    if (region) return region.toUpperCase();
  } catch {
    // navigator unavailable
  }
  return countries.some((c) => c.code === "CZ") ? "CZ" : countries[0]?.code || "CZ";
}

const isPickup = (m) => m?.carrier === "packeta" && String(m.id).includes("pickup");
const needsAddress = (m) => !!m?.carrier && !String(m.id).includes("pickup");

export default function CheckoutPage() {
  const { config, sellable, blocked, lines, code, clear, setCode, refresh, catalog } = useStore();
  const navigate = useNavigate();
  const countries = useMemo(() => shipCountries(config), [config]);
  const [form, setForm] = useState(() => {
    const saved = loadCheckoutForm() || {};
    const country = countries.some((c) => c.code === saved.country) ? saved.country : guessCountry(countries, config.visitorCountry);
    return {
      email: "", name: "", phone: "", street: "", city: "", zip: "", note: "", acceptsMarketing: false, ...saved,
      country,
      shippingMethod: countries.find((c) => c.code === country)?.methods.some((m) => m.id === saved.shippingMethod)
        ? saved.shippingMethod
        : countries.find((c) => c.code === country)?.methods[0]?.id || "",
      paymentMethod: country !== "CZ" ? "card" : saved.paymentMethod || (config.cardPayments ? "card" : "bank"),
      acceptTerms: false,
      website: "",
    };
  });
  // From a country we don't ship to: a contact form instead of the checkout.
  const [shipsHere, setShipsHere] = useState(false);
  const outside = !!config.visitorCountry && !shipsHere && (config.countries || []).length > 0 && !config.countries.some((c) => c.code === config.visitorCountry);
  const [pickup, setPickup] = useState(() => loadCheckoutForm()?.pickup || null);
  const [mapOpen, setMapOpen] = useState(false);
  const [noteOpen, setNoteOpen] = useState(!!form.note);
  const [summaryOpen, setSummaryOpen] = useState(false);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  useSeo({ title: "Checkout", noindex: true, path: "/merch/checkout" });

  const destination = countries.find((c) => c.code === form.country) || countries[0];
  const methods = destination?.methods || [];
  const method = methods.find((m) => m.id === form.shippingMethod);
  const abroad = form.country !== "CZ";
  const cantPayAbroad = abroad && !config.cardPayments;
  const items = sellable.map((l) => ({ productId: l.id, qty: l.qty }));
  const { quote, error: quoteError } = useQuote({ items, code, extra: { country: form.country, shippingMethod: form.shippingMethod || undefined }, enabled: !!catalog });
  const total = quote?.totalMinor;
  const nothingToPay = quote && quote.totalMinor === 0;

  // Keep what they typed if they step back to the cart (never the terms tick).
  useEffect(() => {
    const { acceptTerms, website, ...rest } = form;
    saveCheckoutForm({ ...rest, pickup });
  }, [form, pickup]);

  if (catalog && !lines.length && !busy) return <Navigate to="/merch/cart" replace />;
  if (outside) return <InquiryForm onShipsHere={() => setShipsHere(true)} />;

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });
  const setCountry = (code) => {
    const next = countries.find((c) => c.code === code);
    setForm({
      ...form,
      country: code,
      shippingMethod: next?.methods.some((m) => m.id === form.shippingMethod) ? form.shippingMethod : next?.methods[0]?.id || "",
      paymentMethod: code !== "CZ" ? "card" : form.paymentMethod,
    });
    if (pickup && pickup.country && pickup.country !== code) setPickup(null);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (isPickup(method) && !pickup) {
      setError("Choose a pickup point first");
      document.getElementById("sh-delivery")?.scrollIntoView({ behavior: "smooth", block: "center" });
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const res = await storeApi.checkout({
        items,
        email: form.email,
        name: form.name,
        phone: form.phone,
        country: form.country,
        shippingMethod: form.shippingMethod,
        address: needsAddress(method) ? { street: form.street, city: form.city, zip: form.zip, name: form.name, country: form.country } : undefined,
        pickupPoint: isPickup(method) ? pickup : undefined,
        discountCode: code || undefined,
        note: form.note,
        acceptTerms: form.acceptTerms,
        acceptsMarketing: form.acceptsMarketing,
        paymentMethod: form.paymentMethod,
        website: form.website,
      });
      clear();
      setCode("");
      saveCheckoutForm({ email: form.email, name: form.name, phone: form.phone, country: form.country });
      if (res.payment?.type === "card" && res.payment.checkoutUrl) window.location.href = res.payment.checkoutUrl;
      else navigate(`/merch/order/${res.token}?placed=1`, { replace: true });
    } catch (err) {
      setError(err.message);
      setBusy(false);
      if (err.status === 409) refresh();
    }
  };

  const payLabel = nothingToPay ? "Place order" : form.paymentMethod === "card" ? `Pay ${total != null ? money(total) : ""}` : `Place order${total != null ? ` · ${money(total)}` : ""}`;

  return (
    <div className="sh-wrap sh-checkout">
      <header className="sh-pagehead">
        <h1 className="sh-h1">Checkout</h1>
        <Link className="sh-textlink" to="/merch/cart">
          <Icon name="left" size={14} /> Back to cart
        </Link>
      </header>

      <button className="sh-sumtoggle" onClick={() => setSummaryOpen(!summaryOpen)} aria-expanded={summaryOpen}>
        <span>
          {summaryOpen ? "Hide" : "Show"} order summary <Icon name="down" size={14} className={summaryOpen ? "flip" : ""} />
        </span>
        <strong>{total != null ? money(total) : ""}</strong>
      </button>

      <div className="sh-checkout-grid">
        <form className="sh-form" onSubmit={submit} noValidate={false}>
          <input name="website" value={form.website} onChange={set("website")} className="sh-hp" tabIndex={-1} autoComplete="off" aria-hidden="true" />
          {blocked.length > 0 && (
            <div className="sh-alert" role="alert">
              <p>Some items in your cart sold out and won't be in this order.</p>
              <Link className="sh-textlink" to="/merch/cart">
                Review cart
              </Link>
            </div>
          )}

          <section className="sh-step">
            <h2 className="sh-step-title">
              <span>1</span> Contact
            </h2>
            <Field label="Email" hint="Only for this order and its delivery">
              <input type="email" required value={form.email} onChange={set("email")} autoComplete="email" inputMode="email" />
            </Field>
            <Checkbox className="sh-cb-block" checked={form.acceptsMarketing} onChange={set("acceptsMarketing")}>
              Email me about new drops and shows
            </Checkbox>
          </section>

          <section className="sh-step" id="sh-delivery">
            <h2 className="sh-step-title">
              <span>2</span> Delivery
            </h2>
            <div className="sh-field">
              <span className="sh-field-label" id="sh-country-label">
                Country
              </span>
              <Select
                labelledBy="sh-country-label"
                label="Country"
                value={form.country}
                onChange={setCountry}
                options={countries.map((c) => ({ value: c.code, label: countryName(c.code), prefix: flag(c.code) }))}
              />
            </div>
            <div className="sh-methods" role="radiogroup" aria-label="Delivery method">
              {methods.map((m) => {
                const info = deliveryInfo({ methodId: m.id, carrier: m.carrier, country: form.country });
                const on = form.shippingMethod === m.id;
                const free = quote?.shipping?.method?.id === m.id && quote.shippingMinor === 0 && m.priceMinor > 0;
                return (
                  <label key={m.id} className={`sh-method${on ? " on" : ""}`}>
                    <input type="radio" name="ship" checked={on} onChange={() => setForm({ ...form, shippingMethod: m.id })} />
                    <ShippingIcon methodId={m.id} carrier={m.carrier} size={40} />
                    <span className="sh-method-text">
                      <strong>{m.label}</strong>
                      {info.brand && !m.label.includes(info.brand) && <span>{info.brand}</span>}
                      {on && info.how && <span className="sh-method-how">{info.how}</span>}
                    </span>
                    <em>{free || !m.priceMinor ? "Free" : money(m.priceMinor)}</em>
                  </label>
                );
              })}
            </div>
            {!methods.length && <p className="sh-error">We can't deliver there right now.</p>}

            {isPickup(method) && (
              <div className={`sh-point${pickup ? " has" : ""}`}>
                {pickup ? (
                  <>
                    <Icon name="pin" size={20} />
                    <div>
                      <strong>{pickup.name}</strong>
                      <p>{pickup.address}</p>
                    </div>
                    <button type="button" className="sh-textlink" onClick={() => setMapOpen(true)}>
                      Change
                    </button>
                  </>
                ) : config.packetaWidgetKey ? (
                  <button type="button" className="sh-btn sh-btn-ghost sh-btn-wide" onClick={() => setMapOpen(true)}>
                    <Icon name="pin" size={18} /> Choose a pickup point
                  </button>
                ) : (
                  <p className="sh-muted">Pickup points can't be picked right now. Please choose home delivery.</p>
                )}
              </div>
            )}

            <div className="sh-fields">
              <Field label="Full name" hint={isPickup(method) ? "As on your ID, for collecting the parcel" : null}>
                <input required value={form.name} onChange={set("name")} autoComplete="name" />
              </Field>
              {needsAddress(method) && (
                <>
                  <Field label="Street and number">
                    <input required value={form.street} onChange={set("street")} autoComplete="street-address" />
                  </Field>
                  <div className="sh-two">
                    <Field label="City">
                      <input required value={form.city} onChange={set("city")} autoComplete="address-level2" />
                    </Field>
                    <Field label="Postcode">
                      <input required value={form.zip} onChange={set("zip")} autoComplete="postal-code" inputMode={form.country === "CZ" || form.country === "SK" ? "numeric" : undefined} />
                    </Field>
                  </div>
                </>
              )}
              <Field label={abroad ? "Phone" : "Phone (optional)"} hint={abroad ? "With the country code. The courier will need it." : "Zásilkovna texts you when it's ready"}>
                <input type="tel" required={abroad} value={form.phone} onChange={set("phone")} autoComplete="tel" placeholder={abroad ? "+49 …" : "+420 …"} />
              </Field>
            </div>
          </section>

          {!nothingToPay && (
            <section className="sh-step">
              <h2 className="sh-step-title">
                <span>3</span> Payment
              </h2>
              {cantPayAbroad ? (
                <p className="sh-error">We can't take orders from abroad right now, sorry. Please try again later.</p>
              ) : (
                <div className="sh-methods" role="radiogroup" aria-label="Payment method">
                  {config.cardPayments && (
                    <label className={`sh-method${form.paymentMethod === "card" ? " on" : ""}`}>
                      <input type="radio" name="pay" checked={form.paymentMethod === "card"} onChange={() => setForm({ ...form, paymentMethod: "card" })} />
                      <span className="sh-method-icon">
                        <Icon name="lock" size={20} />
                      </span>
                      <span className="sh-method-text">
                        <strong>Card, Apple Pay, Google Pay</strong>
                        {form.paymentMethod === "card" && <span className="sh-method-how">You'll pay on SumUp's secure page and come straight back here.</span>}
                      </span>
                    </label>
                  )}
                  {!abroad && (
                    <label className={`sh-method${form.paymentMethod === "bank" ? " on" : ""}`}>
                      <input type="radio" name="pay" checked={form.paymentMethod === "bank"} onChange={() => setForm({ ...form, paymentMethod: "bank" })} />
                      <span className="sh-method-icon sh-qr-glyph" aria-hidden="true">
                        QR
                      </span>
                      <span className="sh-method-text">
                        <strong>Bank transfer</strong>
                        {form.paymentMethod === "bank" && <span className="sh-method-how">Scan a QR code in your banking app. We hold your items while you pay and confirm automatically.</span>}
                      </span>
                    </label>
                  )}
                  {abroad && <p className="sh-muted sh-small">Orders from outside Czechia are paid by card.</p>}
                </div>
              )}
            </section>
          )}

          <section className="sh-step sh-step-last">
            {noteOpen ? (
              <Field label="Note for us (optional)">
                <textarea rows={3} value={form.note} onChange={set("note")} maxLength={1000} />
              </Field>
            ) : (
              <button type="button" className="sh-textlink" onClick={() => setNoteOpen(true)}>
                + Add a note
              </button>
            )}
            <Checkbox className="sh-cb-block" checked={form.acceptTerms} onChange={set("acceptTerms")} required>
              I agree to the <Link to="/merch/terms" target="_blank">terms</Link> and the{" "}
              <Link to="/merch/returns" target="_blank">
                {config.returnWindowDays || 14}-day return policy
              </Link>
              .
            </Checkbox>
            {(error || (quoteError && !quote)) && (
              <div className="sh-alert" role="alert">
                <p>{error || quoteError?.message}</p>
                {/sold out|available/i.test(error || "") && (
                  <Link className="sh-textlink" to="/merch/cart">
                    Review cart
                  </Link>
                )}
              </div>
            )}
            <button className="sh-btn sh-btn-xl" disabled={busy || !method || cantPayAbroad || !sellable.length}>
              {busy ? "Placing your order…" : (
                <>
                  <Icon name="lock" size={16} /> {payLabel}
                </>
              )}
            </button>
            <p className="sh-summary-note">
              {form.paymentMethod === "card" && !nothingToPay ? "Payments are handled by SumUp. We never see your card details." : "You'll see the payment details on the next page."}
            </p>
          </section>
        </form>

        <aside className={`sh-summary sh-co-summary${summaryOpen ? " open" : ""}`}>
          <ul className="sh-mini">
            {sellable.map((l) => {
              const q = quote?.lines.find((x) => x.productId === l.id);
              const opts = Object.values(l.options || {}).filter(Boolean).join(" / ");
              return (
                <li key={l.id}>
                  <span className="sh-mini-img">
                    {l.image && <img src={l.image} alt="" />}
                    <b>{l.qty}</b>
                  </span>
                  <span className="sh-mini-text">
                    <strong>{l.title}</strong>
                    {opts && <span>{opts}</span>}
                  </span>
                  <span>{money((q ? q.unitPriceMinor : l.priceMinor) * l.qty)}</span>
                </li>
              );
            })}
          </ul>
          <dl className="sh-totals">
            <div>
              <dt>Subtotal</dt>
              <dd>{quote ? money(quote.subtotalMinor) : "…"}</dd>
            </div>
            {quote?.discountMinor > 0 && (
              <div className="sh-total-discount">
                <dt>{quote.discount?.code || quote.discount?.name || "Discount"}</dt>
                <dd>−{money(quote.discountMinor)}</dd>
              </div>
            )}
            <div>
              <dt>Delivery</dt>
              <dd>{quote?.shipping ? (quote.shippingMinor ? money(quote.shippingMinor) : "Free") : "—"}</dd>
            </div>
            <div className="sh-total">
              <dt>Total</dt>
              <dd>{total != null ? money(total) : "…"}</dd>
            </div>
          </dl>
          {!code && (
            <p className="sh-muted sh-small">
              Discount code? Add it in the <Link to="/merch/cart">cart</Link>.
            </p>
          )}
        </aside>
      </div>

      {mapOpen && config.packetaWidgetKey && (
        <PacketaPicker
          apiKey={config.packetaWidgetKey}
          country={form.country}
          vendors={destination?.pickupVendors}
          onPick={(p) => {
            setPickup(p);
            setMapOpen(false);
            setError(null);
          }}
          onClose={() => setMapOpen(false)}
        />
      )}
    </div>
  );
}

function Field({ label, hint, children }) {
  return (
    <label className="sh-field">
      <span className="sh-field-label">{label}</span>
      {children}
      {hint && <span className="sh-field-hint">{hint}</span>}
    </label>
  );
}
